import { motion } from 'framer-motion'
import { Link, Routes, Route } from 'react-router-dom'
import { HelpCircle, Mail, User, ArrowRight, Search, MessageCircle, Phone, CheckCircle } from 'lucide-react'
import { navigationData } from '../utils/navigation'

const supportCategories = [
  {
    icon: HelpCircle,
    title: 'Help Center',
    description: 'Find answers to common questions, troubleshooting guides, and system status.',
    href: '/support/help',
    gradient: 'from-synth-500 to-synth-700',
  },
  {
    icon: Mail,
    title: 'Contact',
    description: 'Reach our technical support, sales team, or partnership inquiries.',
    href: '/support/contact',
    gradient: 'from-signal-500 to-synth-600',
  },
  {
    icon: User,
    title: 'Account',
    description: 'Manage billing, subscriptions, and security settings.',
    href: '/support/account',
    gradient: 'from-ember-500 to-synth-600',
  },
]

const popularTopics = [
  { title: 'Getting started with the API', href: '/support/help/faq' },
  { title: 'Billing and invoices', href: '/support/account/billing' },
  { title: 'Model usage limits', href: '/support/help/faq' },
  { title: 'Security best practices', href: '/support/account/security' },
  { title: 'Troubleshooting errors', href: '/support/help/troubleshoot' },
]

function SupportIndex() {
  return (
    <div className="pt-24 pb-16">
      {/* Hero */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-synth-950/30 to-void-950" />
        <div className="absolute inset-0 grid-overlay opacity-30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold text-void-50 mb-6">
              How Can We <span className="text-gradient">Help?</span>
            </h1>
            <p className="text-xl text-void-400 max-w-2xl mx-auto mb-8">
              Get the support you need, when you need it.
            </p>
            
            {/* Search */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-void-500" />
              <input
                type="text"
                placeholder="Search for help..."
                className="input-field pl-12 text-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Categories */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {supportCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={category.href}
                  className="group block h-full p-8 rounded-3xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${category.gradient} mb-6`}>
                    <category.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h2 className="text-2xl font-display font-bold text-void-50 mb-3 group-hover:text-synth-300 transition-colors">
                    {category.title}
                  </h2>
                  <p className="text-void-400">{category.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Popular Topics & Contact Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Popular Topics */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-display font-bold text-void-50 mb-6">Popular Topics</h2>
              <div className="space-y-3">
                {popularTopics.map((topic) => (
                  <Link
                    key={topic.title}
                    to={topic.href}
                    className="flex items-center justify-between p-4 rounded-xl bg-void-900/30 border border-void-800/50 hover:border-synth-700/50 transition-all group"
                  >
                    <span className="text-void-300 group-hover:text-void-100 transition-colors">
                      {topic.title}
                    </span>
                    <ArrowRight className="w-4 h-4 text-void-600 group-hover:text-synth-400 transition-colors" />
                  </Link>
                ))}
              </div>
            </motion.div>
            
            {/* Contact Options */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl font-display font-bold text-void-50 mb-6">Get in Touch</h2>
              <div className="space-y-4">
                <div className="p-6 rounded-xl bg-void-900/30 border border-void-800/50">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-synth-500/20">
                      <MessageCircle className="w-5 h-5 text-synth-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-void-100 mb-1">Live Chat</h3>
                      <p className="text-sm text-void-400 mb-2">Chat with our support team in real-time.</p>
                      <span className="flex items-center gap-2 text-emerald-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        Available 24/7
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 rounded-xl bg-void-900/30 border border-void-800/50">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-signal-500/20">
                      <Phone className="w-5 h-5 text-signal-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-void-100 mb-1">Phone Support</h3>
                      <p className="text-sm text-void-400 mb-2">Speak directly with a support specialist.</p>
                      <span className="text-void-300 text-sm">1-800-SYNTH-AI</span>
                    </div>
                  </div>
                </div>
                
                <div className="p-6 rounded-xl bg-void-900/30 border border-void-800/50">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-ember-500/20">
                      <Mail className="w-5 h-5 text-ember-400" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-void-100 mb-1">Email</h3>
                      <p className="text-sm text-void-400 mb-2">Send us a detailed inquiry.</p>
                      <span className="text-void-300 text-sm">support@synthetiqsignals.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

function CategoryPage({ category }) {
  const data = navigationData.support.categories.find(c => c.href.includes(category))
  
  if (!data) return <SupportIndex />
  
  return (
    <div className="pt-24 pb-16">
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-void-50 mb-4">
              {data.name}
            </h1>
            <p className="text-xl text-void-400 mb-12">Find help with {data.name.toLowerCase()}</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {data.items.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={item.href}
                    className="block p-6 rounded-2xl bg-void-900/50 border border-void-800/50 hover:border-synth-700/50 transition-all group"
                  >
                    <h3 className="text-lg font-display font-semibold text-void-100 mb-2 group-hover:text-synth-300 transition-colors">
                      {item.name}
                    </h3>
                    <p className="text-void-400 text-sm">{item.description}</p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default function SupportPage() {
  return (
    <Routes>
      <Route index element={<SupportIndex />} />
      <Route path="help/*" element={<CategoryPage category="help" />} />
      <Route path="contact/*" element={<CategoryPage category="contact" />} />
      <Route path="account/*" element={<CategoryPage category="account" />} />
    </Routes>
  )
}
